import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';
import Button from '../common/Button';

const BaselineQuestionnaire = ({ clientId, onComplete }) => {
    // Risposte qualitative + scale 1-4
    const [answers, setAnswers] = useState({
        motivazione: '',
        aspettative: '',
        difficolta: '',
        risorse: '',
        supporti: '',
        consapevolezza: 2,
        gestione: 2,
        contatto: 2,
        soddisfazione: 2,
        techDirezione: '',
        techScudo: '',
        techDelega: '',
        techJournaling: ''
    });

    // Wheel of Life Scores (8 areas)
    const [wheelScores, setWheelScores] = useState({
        Lavoro: 5,
        Famiglia: 5,
        Relazioni: 5,
        Salute: 5,
        Emozioni: 5,
        Finanze: 5,
        Crescita: 5,
        Ambiente: 5
    });

    const [currentStep, setCurrentStep] = useState(1);

    const [customLabels, setCustomLabels] = useState({});

    useEffect(() => {
        const loadClient = async () => {
            const client = await DataManager.getClientById(clientId);
            if (client && client.wheelLabels) {
                setCustomLabels(client.wheelLabels);
            }
        };
        loadClient();
    }, [clientId]);

    const wheelAreas = [
        { key: 'Lavoro', icon: '💼', label: customLabels.Lavoro || 'Lavoro/Carriera' },
        { key: 'Famiglia', icon: '👨‍👩‍👧‍👦', label: customLabels.Famiglia || 'Famiglia' },
        { key: 'Relazioni', icon: '💕', label: customLabels.Relazioni || 'Relazioni Sociali' },
        { key: 'Salute', icon: '🏥', label: customLabels.Salute || 'Salute Fisica' },
        { key: 'Emozioni', icon: '🎭', label: customLabels.Emozioni || 'Benessere Emotivo' },
        { key: 'Finanze', icon: '💰', label: customLabels.Finanze || 'Situazione Finanziaria' },
        { key: 'Crescita', icon: '📚', label: customLabels.Crescita || 'Crescita Personale' },
        { key: 'Ambiente', icon: '🌍', label: customLabels.Ambiente || 'Ambiente/Tempo Libero' }
    ];

    const handleSubmit = async () => {
        try {
            // Salva i dati
            const baseline = await DataManager.saveBaselineAssessment(
                clientId,
                'T0',
                null,
                answers,
                wheelScores
            );

            await DataManager.updateClient(clientId, { hasCompletedOnboarding: true });

            alert('✅ Questionario completato! Grazie per aver condiviso queste informazioni.');

            if (onComplete) {
                onComplete(baseline);
            }
        } catch (error) {
            console.error('Error saving baseline:', error);
            alert('❌ Errore nel salvataggio. Riprova: ' + error.message);
        }
    };

    const inputClasses = "w-full p-4 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition resize-none bg-white font-medium text-gray-800";

    return (
        <div className="max-w-4xl mx-auto p-6">
            {/* Progress Bar */}
            <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-600">Progresso</span>
                    <span className="text-sm font-medium text-blue-600">{currentStep}/3</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(currentStep / 3) * 100}%` }}
                    ></div>
                </div>
            </div>

            {/* Step 1: M&A + R&C */}
            {currentStep === 1 && (
                <div className="bg-white rounded-3xl shadow-lg p-8 animate-fade-in">
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">📸 Questionario iniziale (1/3)</h2>
                    <p className="text-gray-600 mb-8">Definiamo il "contratto" e la fotografia del tuo stato attuale.</p>

                    <div className="space-y-8">
                        <div>
                            <h3 className="text-xl font-bold text-indigo-900 border-b-2 border-indigo-100 pb-2 mb-4">1. Motivazione e Aspettative (La Figura)</h3>
                            <p className="text-sm text-gray-600 mb-6">Queste domande servono a capire cosa emerge dallo sfondo della tua vita in questo momento.</p>
                            
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Cosa ti ha spinto a cercare supporto proprio ora?</label>
                                    <p className="text-xs text-gray-500 mb-2">Specifica se c'è stato un evento particolare (lutto, separazione, crisi lavorativa, ecc.).</p>
                                    <textarea rows="3" className={inputClasses} value={answers.motivazione} onChange={e => setAnswers({...answers, motivazione: e.target.value})}></textarea>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Cosa speri concretamente di ottenere o cambiare grazie a questo percorso?</label>
                                    <textarea rows="3" className={inputClasses} value={answers.aspettative} onChange={e => setAnswers({...answers, aspettative: e.target.value})}></textarea>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Quali sono le tue principali aree di sofferenza o difficoltà attuale?</label>
                                    <textarea rows="3" className={inputClasses} value={answers.difficolta} onChange={e => setAnswers({...answers, difficolta: e.target.value})}></textarea>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xl font-bold text-emerald-900 border-b-2 border-emerald-100 pb-2 mb-4 mt-8">2. Risorse e Campo</h3>
                            <p className="text-sm text-gray-600 mb-6">Identifichiamo i tuoi punti di forza per sostenere il processo di cambiamento.</p>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Quali risorse personali senti di avere?</label>
                                    <p className="text-xs text-gray-500 mb-2">Capacità, talenti, attitudini che ti aiutano nelle difficoltà.</p>
                                    <textarea rows="3" className={inputClasses} value={answers.risorse} onChange={e => setAnswers({...answers, risorse: e.target.value})}></textarea>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Su quali supporti esterni puoi contare?</label>
                                    <p className="text-xs text-gray-500 mb-2">Relazioni, passioni, interessi, contesto sociale.</p>
                                    <textarea rows="3" className={inputClasses} value={answers.supporti} onChange={e => setAnswers({...answers, supporti: e.target.value})}></textarea>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex justify-end">
                        <Button onClick={() => setCurrentStep(2)} className="px-8 py-3">Continua →</Button>
                    </div>
                </div>
            )}

            {/* Step 2: Scale SIBIG e Rapporto Tech */}
            {currentStep === 2 && (
                <div className="bg-white rounded-3xl shadow-lg p-8 animate-fade-in">
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">📸 Questionario iniziale (2/3)</h2>
                    <p className="text-gray-600 mb-8">Valutiamo la tua consapevolezza e il tuo rapporto con la dimensione digitale.</p>

                    <div className="space-y-8">
                        <div>
                            <h3 className="text-xl font-bold text-purple-900 border-b-2 border-purple-100 pb-2 mb-4">3. Consapevolezza e Contatto</h3>
                            <p className="text-sm text-gray-500 mb-6 font-medium bg-purple-50 p-3 rounded-lg border border-purple-100">
                                Valuta il tuo stato attuale per poter misurare i progressi (Scala 1-4 dove: 1=Per niente, 4=Moltissimo)
                            </p>
                            
                            <div className="space-y-6">
                                {[
                                    { key: 'consapevolezza', label: 'Quanto sono consapevole dei miei pensieri e delle mie emozioni?' },
                                    { key: 'gestione', label: 'Quanto mi sento in grado di gestire (o controllare) i miei vissuti e la mia vita?' },
                                    { key: 'contatto', label: 'Quanto sono in contatto con il mio corpo in questo momento?' },
                                    { key: 'soddisfazione', label: 'Quanto sono globalmente soddisfatto della mia vita attuale?' }
                                ].map(item => (
                                    <div key={item.key} className="bg-gray-50 p-5 rounded-2xl border border-gray-100 shadow-sm">
                                        <label className="block text-md font-bold text-gray-800 mb-4">{item.label}</label>
                                        <div className="grid grid-cols-4 gap-3">
                                            {[1,2,3,4].map(val => (
                                                <button 
                                                    key={val}
                                                    onClick={() => setAnswers({...answers, [item.key]: val})}
                                                    className={`py-3 rounded-xl font-bold text-lg transition flex items-center justify-center border-2 
                                                        ${answers[item.key] === val ? 'bg-purple-600 text-white border-purple-600 shadow-md transform scale-105' : 'bg-white text-gray-500 border-gray-200 hover:border-purple-300 hover:bg-purple-50'}`}
                                                >
                                                    {val}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xl font-bold text-teal-900 border-b-2 border-teal-100 pb-2 mb-4 mt-8">4. Rapporto con la Tecnologia (Integrazione AwarenessTool)</h3>
                            <p className="text-sm text-gray-600 mb-6">In questa sezione indaghiamo se il digitale è un "muro" o un "ponte" verso il tuo sentire.</p>
                            
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Direzione dell'ascolto</label>
                                    <p className="text-xs text-gray-500 mb-2">Quando provi un disagio, il tuo primo impulso è ascoltare il corpo o cercare subito una risposta/distrazione sullo smartphone (Google, Social, AI)?</p>
                                    <textarea rows="2" className={inputClasses} value={answers.techDirezione} onChange={e => setAnswers({...answers, techDirezione: e.target.value})}></textarea>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Tecnologia come scudo</label>
                                    <p className="text-xs text-gray-500 mb-2">Ti capita di usare il web o le app per "anestetizzare" un'emozione fastidiosa o per evitare di stare nel vuoto?</p>
                                    <textarea rows="2" className={inputClasses} value={answers.techScudo} onChange={e => setAnswers({...answers, techScudo: e.target.value})}></textarea>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Delega del significato</label>
                                    <p className="text-xs text-gray-500 mb-2">Hai l'abitudine di cercare online "consigli psicologici" o interpretazioni ai tuoi stati d'animo invece di parlarne in sessione?</p>
                                    <textarea rows="2" className={inputClasses} value={answers.techDelega} onChange={e => setAnswers({...answers, techDelega: e.target.value})}></textarea>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Disponibilità al journaling</label>
                                    <p className="text-xs text-gray-500 mb-2">Ti senti a tuo agio nell'usare uno spazio digitale (diario, mappe) per tracciare i tuoi vissuti tra un incontro e l'altro?</p>
                                    <textarea rows="2" className={inputClasses} value={answers.techJournaling} onChange={e => setAnswers({...answers, techJournaling: e.target.value})}></textarea>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex justify-between">
                        <button onClick={() => setCurrentStep(1)} className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition font-medium">← Indietro</button>
                        <Button onClick={() => setCurrentStep(3)} className="px-8 py-3">Continua →</Button>
                    </div>
                </div>
            )}

            {/* Step 3: Ruota della Vita */}
            {currentStep === 3 && (
                <div className="bg-white rounded-3xl shadow-lg p-8 animate-fade-in">
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">🎯 Ruota della Vita (3/3)</h2>
                    <p className="text-gray-600 mb-6">
                        Valuta da 1 a 10 il tuo livello di soddisfazione in ciascuna area della tua vita.
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                        1 = Per niente soddisfatto | 10 = Completamente soddisfatto
                    </p>

                    <div className="space-y-6">
                        {wheelAreas.map(area => (
                            <div key={area.key} className="bg-gray-50 p-6 rounded-2xl">
                                <div className="flex justify-between items-center mb-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-3xl">{area.icon}</span>
                                        <div>
                                            <h3 className="font-bold text-gray-800">{area.label}</h3>
                                            <p className="text-sm text-gray-500">{area.key}</p>
                                        </div>
                                    </div>
                                    <div className="text-3xl font-bold text-blue-600">
                                        {wheelScores[area.key]}
                                    </div>
                                </div>

                                {/* Slider */}
                                <input
                                    type="range"
                                    min="1"
                                    max="10"
                                    value={wheelScores[area.key]}
                                    onChange={(e) => setWheelScores({ ...wheelScores, [area.key]: parseInt(e.target.value) })}
                                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                    style={{
                                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((wheelScores[area.key] - 1) / 9) * 100}%, #e5e7eb ${((wheelScores[area.key] - 1) / 9) * 100}%, #e5e7eb 100%)`
                                    }}
                                />

                                {/* Labels */}
                                <div className="flex justify-between text-xs text-gray-400 mt-2">
                                    <span>1</span>
                                    <span>5</span>
                                    <span>10</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 flex justify-between">
                        <button
                            onClick={() => setCurrentStep(2)}
                            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition font-medium"
                        >
                            ← Indietro
                        </button>
                        <Button onClick={handleSubmit} className="px-8 py-3 bg-green-600 hover:bg-green-700">
                            ✅ Completa Questionario
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BaselineQuestionnaire;
